import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import {
  messagePathKey,
  TopicIdToMessagePaths,
} from "../../DataServer/messaging";
import * as components from "../components";
import { BabylonContext, BabylonState } from "../state";
import { extractValues } from "../systems/reloadStaleComponents/reloadVectorFields";

/**
 * Loads the timeline for any bound joint whose cache has gone stale.
 *
 * `reloadRotations` minus the quaternion, and deliberately so: a joint value is
 * one scalar per instant, which is what every other bound channel in this panel
 * already is. Reusing that machinery is the whole answer to "the value may be
 * topic data changing per frame" — the fetch, the sample-and-hold timeline and
 * the abort bookkeeping all come across unchanged.
 *
 * Unlike a position or a rotation, nothing downstream is invalidated: a joint
 * moves nodes *inside* one model, below the anchor `applyRenderOrigin` writes.
 * `resolveGlobalPositions` composes only the entity hierarchy and never sees
 * them, so no global position goes stale when one of these lands.
 */
export function reloadStaleJointValues(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    for (const cache of Object.values(entity.mesh?.jointCaches ?? {})) {
      if (cache.load.state !== "stale") {
        continue;
      }
      cache.load.abortController.abort();

      cache.load.state = "loading";
      const abortController = new AbortController();
      cache.load.abortController = abortController;
      cache.logtimes = [];
      cache.values = [];

      reloadCache(cache, state.context, abortController.signal).catch(
        (e: unknown) => {
          if (isAbortError(e) && abortController.signal.aborted) {
            return;
          }
          ErrorMonitoringService.captureError(e);
        },
      );
    }
  }
}

/**
 * Loads one joint's timeline.
 *
 * Takes the cache rather than the whole state, like `reloadRotations` does, so a
 * binding replaced during the async gap cannot have this write land on it.
 */
async function reloadCache(
  cache: components.JointValueCache,
  context: BabylonContext,
  signal: AbortSignal,
) {
  const { value, scale } = cache.spec;

  // A constant: one sample, and no reason to talk to the data server.
  if (typeof value === "number") {
    cache.logtimes = [0n];
    cache.values = [value * scale];
    cache.load.state = "loaded";
    return;
  }

  const messagePath = {
    id: value.messagePath.id,
    parts: value.messagePath.parts,
  };
  const topicIdToMessagePaths: TopicIdToMessagePaths = {
    [value.topic.id]: { [messagePathKey(messagePath)]: messagePath },
  };

  const topicIdToMessagePathValues = await context.dataServer.getAllData(
    topicIdToMessagePaths,
    signal,
  );
  signal.throwIfAborted();

  const { logtimes, values } = extractValues(
    topicIdToMessagePathValues,
    value,
    scale,
  );
  cache.logtimes = logtimes;
  cache.values = values;
  cache.load.state = "loaded";
}
